/*
* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2022, Bulb Inc. For more information, please email contact@hellobulb.com.
*/

import {
  Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output,
} from '@angular/core';
import { MenuItem } from 'src/app/menus/types';

import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subscription } from 'rxjs/internal/Subscription';
import { ElementDimensions } from './directives/resize-observer.directive';
import { HttpService } from '../http.service';

import { BulbPageViewTranslationProvider } from '../translation-provider';
import { CommandsFromAjs, PageUnit } from '../types';
import { ShareStatus, ShareTransition, UnitStateStore } from '../../../unit/unit-state.store';
import { AeroDialogService } from '../../../aero/aero-dialog/aero-dialog.service';
import { ButtonStyle } from '../../../aero/aero-button/aero-button.component';
import { TranslationService } from '../../../common/translation.service';

/**
 * A component that renders the page header found at the top of page views.
 * It renders
 * - an image as a background
 * - editable page title, auto-saving the page unit
 * - controls for adding, editing, and removing the background image
 * It also emits an event when the page data is changed, allowing
 * a parent component to update its data after saving changes.
 */
@Component({
  selector: 'bulb-page-header',
  templateUrl: './page-header.component.html',
  styleUrls: ['./page-header.component.scss'],
  providers: [
    BulbPageViewTranslationProvider,
  ],
  standalone: false,
})
export class PageHeaderComponent implements OnInit, OnDestroy {
  @Input() pageUnit: PageUnit;

  @Input() previewStateActive: boolean;

  /**
   * A value passed in from the Angular context.
   * userEditable is true if the current user owns this page.
   */
  @Input() userEditable: boolean = false;

  /**
   * A set of commands passed in from the AJS context.
   */
  @Input() commandsFromAjs: CommandsFromAjs;

  /**
   * Emit the updated pageUnit to the parent component.
   */
  @Output() updatePage: EventEmitter<PageUnit> = new EventEmitter<PageUnit>();

  public editControls: MenuItem[];

  public noImageControls: MenuItem[];

  public formGroup: UntypedFormGroup = new UntypedFormGroup({});

  private formChanges: Subscription = new Subscription();

  /**
   * Variable used to add extra margin above the page title if the title exceeds 1 line
   * Uses the ResizeObserverDirective and is set based on the emitted height exceeding 48px
   * Which is the current element height
   */
  public titleHeightChange: boolean = false;

  constructor(
    private httpSvc: HttpService,
    private unitStateStore: UnitStateStore,
    private aeroDialogService: AeroDialogService,
    private $translate: TranslationService,
    @Inject('BulbErrorService') private bulbErrorService,
    @Inject('BulbUnitService') private bulbUnitService,
  ) {
  }

  ngOnInit() {
    this.createFormControls();
  }

  private createFormControls(): void {
    this.formGroup.addControl('headerEffectType', new UntypedFormControl(this.pageUnit.headerEffectType));
    this.formGroup.addControl('name', new UntypedFormControl(this.pageUnit.name));

    this.formChanges = this.formGroup.valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(600),
      ).subscribe((value) => {
        // If the headerEffectType is what changed, and there haven't been other modifications to the page, and
        // the page was previously published, we want to immediately publish after the save so the new header effect
        // becomes active
        let publishAfterSave = false;
        if (this.pageUnit.headerEffectType !== value.headerEffectType) {
          publishAfterSave = (this.unitStateStore.getShareStatus() === ShareStatus.SHARED);
          this.pageUnit.headerEffectType = value.headerEffectType;
        }

        this.httpSvc.updateUnit(this.pageUnit.id, value).subscribe((pageUnit) => {
          this.updatePage.emit(pageUnit);
          if (publishAfterSave) {
            this.httpSvc.publishUnit(this.pageUnit.id).then(() => {
              this.unitStateStore.transitionShareStatus(ShareTransition.SHARE);
            });
          } else {
            this.unitStateStore.transitionShareStatus(ShareTransition.EDIT);
          }
        });
      });
  }

  public titleSizeChanges(event: ElementDimensions) {
    this.titleHeightChange = event.height > 48;
  }

  ngOnDestroy(): void {
    this.formChanges.unsubscribe();
  }

  openTagsDialog(): void {
    const dialog = this.aeroDialogService.showStudentTagsDialog(null, this.pageUnit);

    const subscription = dialog.onClose.subscribe(() => {
      subscription.unsubscribe();
      window.dispatchEvent(new CustomEvent('refreshView'));
    });
  }

  /**
   * Show Confirm dialog.
   */
  showConfirmDialog(tag) {
    this.aeroDialogService.showDialog({
      icon: 'far fa-trash-alt',
      iconClass: 'aero-alert',
      title: this.$translate.translateInstant('organization_turn_off_integration_modal_title'),
      detail: 'Do you really want to remove this tag from the page?',
      primaryLabel: this.$translate.translateInstant('button_delete'),
      primaryStyle: ButtonStyle.NEGATIVE_ACTION,
      onPrimary: () => {
        this.removeSelectedTag(tag);
      },
    });
  }

  removeSelectedTag(tag: any): void {
    this.httpSvc.removeSelectedTag(this.pageUnit.id, tag.id)
      .subscribe({
        next: () => {
          window.dispatchEvent(new CustomEvent('refreshView'));
        },
        error: (err) => {
          this.bulbErrorService.showError(null, err);
        },
      });
  }
}
